import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usersApi, galleryApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ActivityCard from '../components/ActivityCard';
import { toast } from 'sonner';
import { 
  Star, 
  Heart, 
  Calendar, 
  Instagram,
  Facebook,
  ChevronLeft,
  ChevronDown,
  Twitter,
  ImageIcon,
  Pencil
} from 'lucide-react';

const CreatorProfile = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const { user: currentUser, isAuthenticated } = useAuth();

  const [creator, setCreator] = useState(null);
  const [activities, setActivities] = useState([]);
  const [pastActivities, setPastActivities] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [gallery, setGallery] = useState({ manual: [], from_activities: [] });
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [activeOpen, setActiveOpen] = useState(true);
  const [pastOpen, setPastOpen] = useState(false);

  useEffect(() => {
    fetchCreatorData();
  }, [id]);

  const fetchCreatorData = async () => {
    try {
      const [profileRes, activitiesRes, pastRes, reviewsRes, galleryRes] = await Promise.all([
        usersApi.getProfile(id),
        usersApi.getActivities(id, false),
        usersApi.getActivities(id, true),
        usersApi.getReviews(id),
        galleryApi.get(id)
      ]);

      setCreator(profileRes.data);
      setActivities(activitiesRes.data);
      setPastActivities(pastRes.data.filter(a => !activitiesRes.data.find(b => b.activity_id === a.activity_id)));
      if (activitiesRes.data.length === 0) setPastOpen(true);
      setReviews(reviewsRes.data.reviews);
      setGallery(galleryRes.data);
      
      if (currentUser?.following?.includes(profileRes.data.user_id)) {
        setIsFollowing(true);
      }
    } catch (error) {
      toast.error(t('msg.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para seguir a este creador');
      return;
    }
    try {
      const response = await usersApi.follow(id);
      setIsFollowing(response.data.following);
      setCreator(prev => ({
        ...prev,
        followers_count: prev.followers_count + (response.data.following ? 1 : -1)
      }));
    } catch (error) {
      toast.error(t('msg.error'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <p className="text-gray-500">{t('msg.error')}</p>
      </div>
    );
  }

  const socialLinks = creator.social_links || {};
  const allGalleryPhotos = gallery.manual || [];
  const isCreatorProfile = creator.role === 'creator';

  // User profile (simplified): only name, location, bio
  if (!isCreatorProfile) {
    return (
      <div className="min-h-screen pb-32 md:pb-8 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-6 pt-6">
          <div className="flex items-center justify-between mb-6">
            <Link to="/prelanzamiento" className="inline-flex items-center text-gray-400 hover:text-gray-500">
              <ChevronLeft className="w-5 h-5 mr-1" />Volver
            </Link>
            {isAuthenticated && currentUser?.user_id === creator.user_id && (
              <Link
                to="/profile"
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50 text-xs font-medium transition-colors"
                data-testid="edit-profile-btn"
              >
                <Pencil className="w-3.5 h-3.5" />
                Editar perfil
              </Link>
            )}
          </div>
          <div className="flex flex-col items-center text-center py-8">
            <Avatar className="h-24 w-24 mb-4">
              <AvatarImage src={creator.profile_image} />
              <AvatarFallback className="bg-primary text-white text-3xl">{creator.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
            </Avatar>
            <h1 className="font-rubik text-2xl font-bold text-gray-500 mb-1">{creator.name}</h1>
            {(creator.state || creator.city) && (
              <p className="text-sm text-gray-400 mb-3">{creator.state}{creator.state && creator.city ? ', ' : ''}{creator.city}</p>
            )}
            {creator.bio && <p className="text-gray-400 text-sm max-w-md">{creator.bio}</p>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-32 md:pb-8 bg-white">
      <div className="max-w-3xl mx-auto px-4 md:px-6 pt-6">
        {/* Back button + Edit button */}
        <div className="flex items-center justify-between mb-6">
          <Link 
            to="/prelanzamiento" 
            className="inline-flex items-center text-gray-400 hover:text-gray-500"
            data-testid="back-link"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Volver
          </Link>
          {isAuthenticated && currentUser?.user_id === creator.user_id && (
            <Link
              to="/profile"
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50 text-xs font-medium transition-colors"
              data-testid="edit-profile-btn"
            >
              <Pencil className="w-3.5 h-3.5" />
              Editar perfil
            </Link>
          )}
        </div>

        {/* Profile header */}
        <div className="flex flex-col items-center text-center mb-8">
          <Avatar className="h-24 w-24 mb-4">
            <AvatarImage src={creator.profile_image} />
            <AvatarFallback className="bg-primary text-white text-3xl">
              {creator.name?.charAt(0)?.toUpperCase()}
            </AvatarFallback>
          </Avatar>

          <h1 className="font-rubik text-2xl font-bold text-gray-500 mb-1" data-testid="creator-name">
            {creator.name}
          </h1>
          {(creator.state || creator.city) && (
            <p className="text-sm text-gray-400 mb-2">
              {creator.state}{creator.state && creator.city ? ', ' : ''}{creator.city}
            </p>
          )}
          {creator.bio && (
            <p className="text-gray-400 text-sm max-w-md mb-4">{creator.bio}</p>
          )}

          {/* Stats row */}
          <div className="flex gap-8 mb-4">
            <div className="text-center">
              <p className="font-bold text-lg text-gray-500">{creator.activities_count || 0}</p>
              <p className="text-xs text-gray-400">{t('profile.activities')}</p>
            </div>
            <div className="text-center">
              <p className="font-bold text-lg text-gray-500">{creator.followers_count || 0}</p>
              <p className="text-xs text-gray-400">Seguidores</p>
            </div>
            {creator.avg_rating > 0 && (
              <div className="text-center">
                <div className="flex items-center justify-center gap-1">
                  <Star className="w-4 h-4 text-amber-500 fill-current" />
                  <p className="font-bold text-lg text-gray-500">{creator.avg_rating}</p>
                </div>
                <p className="text-xs text-gray-400">({creator.reviews_count})</p>
              </div>
            )}
          </div>

          {/* Social links - no website */}
          {(socialLinks.instagram || socialLinks.facebook || socialLinks.twitter || socialLinks.tiktok || socialLinks.youtube) && (
            <div className="flex gap-3 mb-4">
              {socialLinks.instagram && (
                <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer"
                  className="p-2 rounded-full border border-gray-200 text-gray-400 hover:text-gray-500 hover:border-gray-300 transition-colors"
                  data-testid="social-instagram">
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {socialLinks.tiktok && (
                <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer"
                  className="p-2 rounded-full border border-gray-200 text-gray-400 hover:text-gray-500 hover:border-gray-300 transition-colors"
                  data-testid="social-tiktok">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                </a>
              )}
              {socialLinks.youtube && (
                <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer"
                  className="p-2 rounded-full border border-gray-200 text-gray-400 hover:text-gray-500 hover:border-gray-300 transition-colors"
                  data-testid="social-youtube">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
              )}
              {socialLinks.facebook && (
                <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer"
                  className="p-2 rounded-full border border-gray-200 text-gray-400 hover:text-gray-500 hover:border-gray-300 transition-colors"
                  data-testid="social-facebook">
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {socialLinks.twitter && (
                <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer"
                  className="p-2 rounded-full border border-gray-200 text-gray-400 hover:text-gray-500 hover:border-gray-300 transition-colors"
                  data-testid="social-twitter">
                  <Twitter className="w-5 h-5" />
                </a>
              )}
            </div>
          )}

          {/* Follow button */}
          {isAuthenticated && currentUser?.user_id !== creator.user_id && (
            <Button
              onClick={handleFollow}
              className={`rounded-full px-6 ${
                isFollowing 
                  ? 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50' 
                  : 'bg-primary hover:bg-primary-hover text-white'
              }`}
              data-testid="follow-creator-btn"
            >
              <Heart className={`w-4 h-4 mr-2 ${isFollowing ? 'fill-current text-red-500' : ''}`} />
              {isFollowing ? t('activity.following') : t('activity.follow')}
            </Button>
          )}
        </div>

        {/* Tabs */}
        <Tabs defaultValue={searchParams.get('tab') || 'activities'} className="w-full">
          <TabsList className="w-full mb-6 bg-gray-100 p-1 rounded-xl">
            <TabsTrigger value="activities" className="flex-1 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm">
              <Calendar className="w-3.5 h-3.5 mr-1 inline" />
              Actividades
            </TabsTrigger>
            <TabsTrigger value="gallery" className="flex-1 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm">
              <ImageIcon className="w-3.5 h-3.5 mr-1 inline" />
              Galería
            </TabsTrigger>
            <TabsTrigger value="reviews" className="flex-1 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm">
              <Star className="w-3.5 h-3.5 mr-1 inline" />
              Reseñas
            </TabsTrigger>
          </TabsList>

          <TabsContent value="activities">
            {activities.length === 0 && pastActivities.length === 0 ? (
              <p className="text-center text-gray-400 py-12">No hay actividades</p>
            ) : (
              <div className="space-y-6">
                {activities.length > 0 && (
                  <div>
                    <button
                      onClick={() => setActiveOpen(prev => !prev)}
                      className="flex items-center justify-between w-full text-left mb-3"
                    >
                      <span className="text-xs font-bold tracking-wider text-slate-500 uppercase">Activas ({activities.length})</span>
                      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${activeOpen ? '' : '-rotate-90'}`} />
                    </button>
                    {activeOpen && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {activities.map((activity) => (
                          <ActivityCard key={activity.activity_id} activity={activity} />
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {pastActivities.length > 0 && (
                  <div>
                    <button
                      onClick={() => setPastOpen(prev => !prev)}
                      className="flex items-center justify-between w-full text-left mb-3"
                    >
                      <span className="text-xs font-bold tracking-wider text-slate-500 uppercase">Pasadas ({pastActivities.length})</span>
                      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${pastOpen ? '' : '-rotate-90'}`} />
                    </button>
                    {pastOpen && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 opacity-70">
                        {pastActivities.map((activity) => (
                          <ActivityCard key={activity.activity_id} activity={activity} />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="gallery">
            {allGalleryPhotos.length === 0 ? (
              <p className="text-center text-gray-400 py-12">No hay fotos en la galería</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {allGalleryPhotos.map((photo, idx) => (
                  <button
                    key={photo.photo_id || `auto-${idx}`}
                    onClick={() => setLightboxImg(photo.image_url)}
                    className="aspect-square rounded-xl overflow-hidden group relative"
                    data-testid={`gallery-photo-${idx}`}
                  >
                    <img 
                      src={photo.image_url} 
                      alt={photo.caption || ''} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {photo.caption && (
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                        <p className="text-white text-xs truncate">{photo.caption}</p>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="reviews">
            {reviews.length === 0 ? (
              <p className="text-center text-gray-400 py-12">No hay reseñas aún</p>
            ) : (
              <div className="space-y-3">
                {reviews.map((review) => (
                  <div key={review.review_id} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <div className="flex items-start gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-primary text-white text-sm">
                          {review.user_name?.charAt(0)?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-500 text-sm">{review.user_name}</span>
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <Star key={`star-${i}`} className={`w-3.5 h-3.5 ${i < review.rating ? 'text-amber-500 fill-current' : 'text-gray-200'}`} />
                            ))}
                          </div>
                        </div>
                        {review.activity_title && (
                          <p className="text-xs text-primary mb-1">{review.activity_title}</p>
                        )}
                        {review.comment && (
                          <p className="text-sm text-gray-400">{review.comment}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Lightbox */}
      {lightboxImg && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxImg(null)}
        >
          <img src={lightboxImg} alt="" className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}
    </div>
  );
};

export default CreatorProfile;
